import React, {useEffect, useState} from 'react'
import {useAuth} from '../../../app/modules/auth'
import {InterpreterOverviewV2} from './interpreter/InterpreterOverviewV2'
import {useQuery} from 'react-query'
import {getTimeZone} from '../../UI/Common/components/core/_requests'
import {TimezoneConfirmationModal} from '../../common/Models/timezone-confirmation-modal/TimezoneConfirmationModal'
import axios from 'axios'
import toaster from '../../../Utils/toaster'
import {useDispatch} from 'react-redux'
import {fetchCustomers} from '../../redux/customerSlice'
import {fetchLanguages} from '../../redux/languagesSlice'
import {AnyAction, ThunkDispatch} from '@reduxjs/toolkit'
import {useLocation, useNavigate} from 'react-router-dom'
import {NotificationBox} from './NotificationBox'
import {IpiVisDashboard} from './IpiVisDashboard/IpiVisDashboard'
import {ClientPortalDashboard} from './ClientPortal/ClientPortalDashboard'
import {OpiDashboard} from './OpiDashboard/OpiDashboard'
import './DashboardMain.scss'

const moment = require('moment-timezone')
const API_URL = process.env.REACT_APP_API_URL

type DashboardView = 'opi' | 'ipi'

const DashboardMain: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const {currentUser, setCurrentUser} = useAuth()

  const [view, setView] = useState<DashboardView>('opi')
  const [isOpenTimezoneConfirmationModal, setIsOpenTimezoneConfirmationModal] = useState(false)
  const [description, setDescription] = useState('')
  const [changeTimeZoneId, setChangeTimeZoneId] = useState(null)
  const dispatch: ThunkDispatch<{}, {}, AnyAction> = useDispatch()

  useEffect(() => {
    if (currentUser?.result?.userType === 'SYSTEM') {
      dispatch(fetchCustomers())
    }
    if (currentUser?.result?.userType !== 'INTERPRETER') {
      dispatch(fetchLanguages())
    }
  }, [dispatch, currentUser?.result?.userType])

  const handleViewChange = (next: DashboardView) => {
    navigate(`/dashboard?view=${next}`)
  }

  useEffect(() => {
    if (currentUser?.result?.userType !== 'SYSTEM') return

    const params = new URLSearchParams(location.search)
    const viewParam = params.get('view')
    // Legacy tab links: 5 = IPI, 6 = OPI
    const tabParam = params.get('tab')

    let next: DashboardView = 'opi'
    if (viewParam === 'ipi' || tabParam === '5') {
      next = 'ipi'
    } else if (viewParam === 'opi' || tabParam === '6') {
      next = 'opi'
    }

    setView(next)
  }, [location.search, currentUser?.result?.userType])

  const currentTime = moment()
  const offsetMinutes = currentTime.utcOffset()
  const hours = Math.floor(Math.abs(offsetMinutes) / 60)
  const minutes = Math.abs(offsetMinutes) % 60
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const formattedHours = hours.toString().padStart(2, '0')
  const formattedMinutes = minutes.toString().padStart(2, '0')
  const formattedOffset = `UTC${sign}${formattedHours}:${formattedMinutes}`

  const {data: timezoneList} = useQuery(
    `Query.timezoneList`,
    () => {
      return getTimeZone()
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
  )

  function extractUtcOffset(description: string | undefined) {
    const regex = /\(UTC([+-]\d{2}:\d{2})\)/
    const match = description?.match(regex)

    if (match) {
      return `UTC${match[1]}`
    } else {
      return 'Invalid format'
    }
  }

  const defaultTimeZone = extractUtcOffset(currentUser?.result?.defaultTimeZone)

  useEffect(() => {
    if (formattedOffset && defaultTimeZone && timezoneList?.data) {
      if (formattedOffset !== defaultTimeZone && currentUser?.notNowTimeZoneChanging === false) {
        const currentTimeZone: any = timezoneList?.data?.find(
          (x: any) => x.identification == formattedOffset
        )
        setChangeTimeZoneId(currentTimeZone?.code)
        setDescription(
          `We noticed your computer to ${currentTimeZone?.name}.Would you like to update your Adastra time zone and meeting hours to match?`
        )
      }
    }
  }, [formattedOffset, defaultTimeZone, timezoneList, currentUser?.notNowTimeZoneChanging])

  const handleChangeTimeZone = async () => {
    try {
      const {data} = await axios.put(
        `${API_URL}/accounts/update-default-timezone/${changeTimeZoneId}`
      )
      if (data?.status == 'S') {
        toaster('success', data?.text)
        if (currentUser) {
          setCurrentUser({...currentUser, notNowTimeZoneChanging: false})
        }
      } else if (data?.status == 'E') {
        toaster('error', data?.text)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsOpenTimezoneConfirmationModal(false)
    }
  }

  const handleNotNowButton = async () => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        notNowTimeZoneChanging: true,
      })
    }
  }

  const timezoneModal = (
    <TimezoneConfirmationModal
      show={isOpenTimezoneConfirmationModal}
      onHide={() => setIsOpenTimezoneConfirmationModal(false)}
      onConfirm={() => handleChangeTimeZone()}
      onClose={() => handleNotNowButton()}
      title={description}
    />
  )

  const isInterpreter = currentUser?.result.userType === 'INTERPRETER'
  const isConsumer = currentUser?.result.userType === 'CONSUMER'

  if (isInterpreter) {
    return (
      <>
        <div className='mb-5 d-flex justify-content-between align-items-start'>
          <div></div>
          <NotificationBox userType={currentUser?.result?.userType} />
        </div>
        <InterpreterOverviewV2 />
        {timezoneModal}
      </>
    )
  }

  if (isConsumer) {
    return (
      <>
        <div className='mb-5 d-flex justify-content-between align-items-start'>
          <div>
            <p className='text-gray-500 fs-5 mb-2'>Welcome back, {currentUser?.result.firstName}</p>
          </div>
          <NotificationBox userType={currentUser?.result?.userType} />
        </div>
        <ClientPortalDashboard />
        {timezoneModal}
      </>
    )
  }

  const cardClass =
    view === 'opi' ? 'opi-dashboard-card' : view === 'ipi' ? 'ipivis-dashboard-card' : ''

  return (
    <>
      <div className={`card mb-5 ${cardClass}`.trim()}>
        <div className={`card-body pt-2${view === 'opi' ? ' opi-tab-body' : ''}`}>
          <div className='dashboard-view-shell'>
            <div className='dashboard-view-toggle'>
              <button
                type='button'
                className={`dashboard-view-toggle__btn${view === 'opi' ? ' is-active' : ''}`}
                onClick={() => handleViewChange('opi')}
              >
                Over The Phone Interpretation
              </button>
              <button
                type='button'
                className={`dashboard-view-toggle__btn${view === 'ipi' ? ' is-active' : ''}`}
                onClick={() => handleViewChange('ipi')}
              >
                IPI &amp; VIS Interpretation
              </button>
            </div>

            {view === 'opi' ? <OpiDashboard /> : <IpiVisDashboard />}
          </div>
        </div>
      </div>
      {timezoneModal}
    </>
  )
}

export {DashboardMain}
